import { Nullable } from '@ps/utils';
import { HealthCheckStatusKey } from '@ps/survey-kernel';

import { SurveyFormState } from '../types';
import { QUOTA_CATEGORY } from '../constants/survey-quotas.constants';

/** One supplier's slice of a quota, as returned by the quota list endpoint. */
export interface SurveyQuotaSupplier {
  supplierRefId: string; // supplier_ref_id
  name: string; // name
  currentTarget: number; // current_target
  currentlyOpen: number; // currently_open
  supplierCurrentlyOpen: number; // sup_currently_open
  fielded: number; // complete_fielded
  completeNeeded: number; // complete_needed
  validClicks: number; // buyer_valid_clks
  inProgress: number; // bip
  lastStartDate: string; // last_start_date
  lastCompleteDate: string; // last_complete_date
  isPdsApplied: boolean; // is_pds_applied
  fee: Nullable<number>; // fee
}

/**
 * The per-supplier subset the update-current-target API returns — targets
 * only, with no name or dates. Merged into the quota's existing suppliers by
 * supplierRefId; see mergeQuotaSupplierTargets.
 */
export interface SurveyQuotaSupplierTarget {
  supplierRefId: string;
  currentTarget: number;
  completeNeeded: number;
}

/**
 * Fields returned by the update-current-target API. Shared by SurveyQuota
 * (as a base) and SurveyQuotaUpdate (as its base too) so the two can't drift
 * out of sync.
 *
 * The rule this base enforces: it may only hold fields that response actually
 * returns, because the reducer copies them over the existing quota on save.
 * Anything the response omits — `inProgress`, `suppliers` — belongs on
 * SurveyQuota instead, or a target/lock update would wipe it.
 */
export interface QuotaFieldedMetrics {
  psQuotaId: string; // ps_quota_id
  locked: boolean; // locked
  fielded: number; // achieved
  goal: number; // current_target
  currentlyOpen: number; // currently_open
  maximum: number; // maximum
  allocationPercentage: number; // percentage
}

/** A quota's own Quota Based Pricing sub-object — mirrors legacy Quotas.qbp. */
export interface SurveyQuotaQbp {
  cpi: number;
  enabled: boolean;
  childSurveyId: number;
}

export interface SurveyQuota extends QuotaFieldedMetrics {
  id: string; // buyer_quota_id
  label: string; // quota_title || criteria condition names
  hasCustomTitle: boolean; // true when dto.quota_title was set manually
  groupKey: string; // group_key
  hasInField: boolean; // set from in_field
  isUnknown: boolean; // isUnknown
  criteria: QuotaCriteria[];
  inProgress: number; // bip
  // The quota's own goal, and the scale legacy measures Fielded vs Goal and the
  // progress bar against — distinct from `maximum`, which carries flex.
  requiredCount: number; // required_count
  validClicks: number; // buyer_valid_clks
  suppliers: SurveyQuotaSupplier[]; // supplier-level distribution
  // Not yet returned by the API — see SurveyQuotaDto.aida_health_status.
  aidaHealthStatus?: HealthCheckStatusKey;
  // Not yet returned by the API — see SurveyQuotaDto.last_start_date/last_complete_date.
  lastStartDate?: string;
  lastCompleteDate?: string;
  // Not yet returned by the API — see SurveyQuotaDto.sup_currently_open.
  supplierCurrentlyOpen?: number;
  qbp?: SurveyQuotaQbp;
}

export interface IndividualQuotaAllocation {
  quantity: number;
  percentage: number;
  min: number;
  max: number;
}

export interface IndividualQuota {
  allocation: IndividualQuotaAllocation;
  criteria: QuotaCriteria[];
}

/**
 * The subset of a quota refreshed from the update-current-target API response.
 * Merged into the existing quota so grouping metadata (label/groupKey) is kept.
 */
export interface SurveyQuotaUpdate extends QuotaFieldedMetrics {
  supplierTargets: readonly SurveyQuotaSupplierTarget[];
}

/** The user's choice in the pro-rata confirmation drawer. */
export type ProRataDecision = 'update-suppliers' | 'leave-as-is';

export const UNKNOWN_QUOTA_GROUP_KEY = 'Unknown';

/** A single-field patch keyed by psQuotaId. */
export type QuotaPatch<TField extends string, TValue> = {
  psQuotaId: string;
} & Record<TField, TValue>;

export type QuotaLockChange = QuotaPatch<'locked', boolean>;
export type QuotaCurrentTargetChange = QuotaPatch<'currentTarget', number>;
export type QuotaCurrentlyOpenChange = QuotaPatch<'currentlyOpen', number>;

/** A single-field patch keyed by both psQuotaId and supplierRefId. */
export type SupplierQuotaPatch<TField extends string, TValue> = {
  psQuotaId: string;
  supplierRefId: string;
} & Record<TField, TValue>;

export type SupplierQuotaCurrentTargetChange = SupplierQuotaPatch<
  'currentTarget',
  number
>;
export type SupplierQuotaCurrentlyOpenChange = SupplierQuotaPatch<
  'currentlyOpen',
  number
>;

export interface QuotaFlexible {
  enable: boolean;
  value: number;
}

export interface QuotaRangeSet {
  from: number;
  to: number;
  units: number;
}

export type SurveyQuotaCategory =
  (typeof QUOTA_CATEGORY)[keyof typeof QUOTA_CATEGORY];

export interface QuotaCriteria {
  qualificationCode: number;
  conditionCodes: string[];
  conditionNames: string[];
  qualificationName: string;
  originalMaximum: number;
  originalMinimum: number;
  originalPercentage: number;
  originalRequiredCount: number;
  originalNumber: number;
  originalFlex: QuotaFlexible;
  // Range-based criteria (e.g. age, income) carry their bounds here instead of discrete
  // conditionCodes/conditionNames — mirrors the DTO's range_sets, unmapped until now.
  rangeMin?: number;
  rangeMax?: number;
  rangeUnit?: string;
  rangeSets?: QuotaRangeSet[];
  // Only ever present on the 232 (child-age-gender) criterion — mirrors QuotaCriterionDto.child_age_unit.
  childAgeUnit?: number;
}

export interface Quota {
  buyerQuotaId: string;
  quotaTitle: string;
  groupKey: string;
  requiredCount: number;
  flexible: QuotaFlexible;
  criteria: QuotaCriteria[];
  percentage: number;
  maximum: number;
  minimum: number;
  quotaCategory: SurveyQuotaCategory;
  currentTarget: number;
  fielded: number;
  individualQuotas: IndividualQuota[];
}

export type SurveyQuotasFormState = SurveyFormState<Quota[]>;

// One qualification's own committed quotas, kept by SurveyQuotasManagerService independently of
// SurveyQualification — quotas are Quota Manager's own state, not a field on the qualification.
export interface QualificationQuotas {
  qualificationCode: number;
  qualificationName: string;
  quotas: readonly Quota[];
}
